import { useEffect, useState, useCallback, useMemo } from 'react'
import { apiTransferList, apiTransferBuy, apiTransferCancel, apiTransferBid, apiTransferBidList } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

type SortKey = 'SELLER_NAME' | 'POSITION' | 'PLAYER_NAME' | 'AGE' | 'OVERALL' | 'STAMINA' | 'POTENTIAL' | 'PROPOSED_SALARY' | 'ASK_PRICE'
type SortDir = 'asc' | 'desc'

interface Listing {
  LISTING_CODE:  number
  PLAYER_CODE:   number
  SELLER_CLUB:   number
  SELLER_NAME:   string
  ASK_PRICE:     number
  LISTED_ROUND:  number
  PLAYER_NAME:   string
  NATIONALITY:   string
  AGE:           number
  POSITION:      string
  SUB_POSITIONS: string
  PAC: number; SHO: number; PAS: number; DRI: number; DEF: number; PHY: number
  REF: number; HAN: number; KIK: number; POS_GK: number
  OVERALL:       number
  STAMINA:       number
  POTENTIAL:     number
  SALARY:        number
  PROPOSED_SALARY: number
  CONTRACT_END:  string
}

interface Props {
  auth:             AuthState
  leagueCode:       number
  myClubCode:       number
  myCash:           number
  canViewPotential: boolean
  nonce?:           number
}

const POSITIONS = ['', 'GK', 'CB', 'LB', 'RB', 'DM', 'CM', 'AM', 'LW', 'RW', 'ST']

export default function Transfer({ auth, leagueCode, myClubCode, myCash, canViewPotential, nonce }: Props) {
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading]   = useState(true)
  const [err,    setErr]        = useState('')
  const [actionMsg, setActionMsg] = useState<{text: string, ok: boolean} | null>(null)
  const [position, setPosition] = useState<string>('')
  const [maxPrice, setMaxPrice] = useState<number>(0)
  const [minOvr,   setMinOvr]   = useState<number>(0)
  const [selected, setSelected] = useState<Listing | null>(null)
  const [sortKey,  setSortKey]  = useState<SortKey>('ASK_PRICE')
  const [sortDir,  setSortDir]  = useState<SortDir>('asc')
  const [bids,     setBids]     = useState<any[]>([])
  const [showBids, setShowBids] = useState(false)
  const [scope,    setScope]    = useState<0 | 1>(0)  // 0=LOCAL, 1=GLOBAL

  async function reloadBids() {
    const r = await apiTransferBidList(auth.token)
    if (r.resultCode !== '00') return
    try { const obj = JSON.parse(r.resultData); setBids(obj.bids ?? []) } catch {}
  }
  useEffect(() => { reloadBids() }, [auth.token, nonce])

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir(k === 'OVERALL' || k === 'POTENTIAL' || k === 'STAMINA' ? 'desc' : 'asc') }
  }
  const arrow = (k: SortKey) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  const reload = useCallback(async () => {
    setLoading(true); setErr('')
    const r = await apiTransferList(auth.token, leagueCode, scope, position, maxPrice, minOvr)
    if (r.resultCode !== '00') { setErr(r.resultMessage || '조회 실패'); setLoading(false); return }
    try {
      const obj = JSON.parse(r.resultData) as { listings: Listing[] }
      setListings(obj.listings ?? [])
    } catch { setErr('응답 파싱 실패') }
    finally { setLoading(false) }
  }, [auth.token, leagueCode, scope, position, maxPrice, minOvr])

  useEffect(() => { reload() }, [reload, nonce])

  const sortedListings = useMemo(() => {
    const arr = [...listings]
    arr.sort((a, b) => {
      const va = a[sortKey] as number | string
      const vb = b[sortKey] as number | string
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va
      const sa = String(va ?? ''), sb = String(vb ?? '')
      return sortDir === 'asc' ? sa.localeCompare(sb, 'ko') : sb.localeCompare(sa, 'ko')
    })
    return arr
  }, [listings, sortKey, sortDir])

  async function handleBid(l: Listing) {
    const min = Math.round(l.ASK_PRICE * 0.5)
    const def = Math.round(l.ASK_PRICE * 0.85)
    const input = window.prompt(`${l.PLAYER_NAME} (ASK ₩${l.ASK_PRICE.toLocaleString()})\n\n입찰가(₩) — 최소 ₩${min.toLocaleString()} (ASK 50%):\n\n* ASK의 85% 이상이면 NPC 수락 확률 70%, 다음 라운드 응답.`, String(def))
    if (!input) return
    const price = Number(input.replace(/[^0-9]/g, ''))
    if (!price || price <= 0) return
    const r = await apiTransferBid(auth.token, l.LISTING_CODE, price)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    if (r.resultCode === '00') reloadBids()
  }

  async function handleCancel(l: Listing) {
    if (!window.confirm(`${l.PLAYER_NAME} 매물을 취소합니다.`)) return
    const r = await apiTransferCancel(auth.token, l.LISTING_CODE)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    if (r.resultCode === '00') reload()
  }

  async function handleBuy(l: Listing) {
    if (l.ASK_PRICE > myCash) {
      setActionMsg({ text: `현금 부족 — 보유 ₩${myCash.toLocaleString()} / 매물 ₩${l.ASK_PRICE.toLocaleString()}`, ok: false })
      return
    }
    if (!window.confirm(`${l.PLAYER_NAME} (${l.POSITION}, OVR ${l.OVERALL})\n매도: ${l.SELLER_NAME}\n이적료: ₩ ${l.ASK_PRICE.toLocaleString()}\n영입 후 주급: ₩ ${l.PROPOSED_SALARY.toLocaleString()}/주 (영구 적용)\n\n영입할까요?`)) return
    setActionMsg({ text: '거래 중…', ok: true })
    const r = await apiTransferBuy(auth.token, l.LISTING_CODE)
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    if (r.resultCode === '00') {
      setSelected(null)
      reload()
    }
  }

  return (
    <div className="page-pad transfer-page">
      <h2>이적시장</h2>
      <div className="scope-tabs">
        <button className={scope === 0 ? 'tab on' : 'tab'} onClick={() => setScope(0)}>로컬 시장 (NPC)</button>
        <button className={scope === 1 ? 'tab on' : 'tab'} onClick={() => setScope(1)}>글로벌 거래소 (사용자 매물)</button>
      </div>
      <div className="transfer-head">
        <div>
          <label>현금</label>
          <strong>₩ {myCash.toLocaleString()}</strong>
        </div>
        <div>
          <label>매물 수</label>
          <strong>{listings.length}건</strong>
        </div>
      </div>

      <section className="card">
        <div className="bid-head">
          <button className="btn-bids-toggle" onClick={() => setShowBids(s => !s)}>
            {showBids ? '▼' : '▶'} 내 입찰 목록 ({bids.length})
          </button>
        </div>
        {showBids && (
          <table className="data-table">
            <thead><tr><th>선수</th><th>매도</th><th>ASK</th><th>입찰가</th><th>상태</th><th>응답R</th></tr></thead>
            <tbody>
              {bids.map((b: any) => (
                <tr key={b.BID_CODE}>
                  <td>{b.PLAYER_NAME} <small>({b.POSITION} {b.OVERALL})</small></td>
                  <td>{b.SELLER_NAME}</td>
                  <td className="num">₩ {Number(b.ASK_PRICE).toLocaleString()}</td>
                  <td className="num">₩ {Number(b.BID_PRICE).toLocaleString()}</td>
                  <td>{b.STATUS === 0 ? '⏳ 대기' : b.STATUS === 1 ? '✅ 수락' : '❌ 거절'}</td>
                  <td>{b.RESPOND_ROUND ?? '-'}</td>
                </tr>
              ))}
              {bids.length === 0 && <tr><td colSpan={6} className="muted">입찰 내역 없음</td></tr>}
            </tbody>
          </table>
        )}
      </section>

      <section className="card transfer-filter">
        <span>포지션:</span>
        <select value={position} onChange={e => setPosition(e.target.value)}>
          {POSITIONS.map(p => <option key={p} value={p}>{p === '' ? '전체' : p}</option>)}
        </select>
        <span>최대 가격(억):</span>
        <input type="number" min={0} value={maxPrice / 100000000 || ''} onChange={e => setMaxPrice(Number(e.target.value) * 100000000)} placeholder="제한 없음" />
        <span>최소 종합:</span>
        <input type="number" min={0} max={99} value={minOvr || ''} onChange={e => setMinOvr(Number(e.target.value))} placeholder="제한 없음" />
        <button onClick={reload}>조회</button>
      </section>

      {actionMsg && <p className={actionMsg.ok ? 'msg ok' : 'msg'}>{actionMsg.text}</p>}

      {loading ? <p>매물 불러오는 중…</p> : err ? <p className="msg">{err}</p> : (
        <div className="transfer-2col">
          <section className="card">
            <table className="data-table transfer-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('SELLER_NAME')}>매도{arrow('SELLER_NAME')}</th>
                  <th onClick={() => toggleSort('POSITION')}>포지션{arrow('POSITION')}</th>
                  <th onClick={() => toggleSort('PLAYER_NAME')}>이름{arrow('PLAYER_NAME')}</th>
                  <th onClick={() => toggleSort('AGE')}>나이{arrow('AGE')}</th>
                  <th onClick={() => toggleSort('OVERALL')}>종합{arrow('OVERALL')}</th>
                  <th onClick={() => toggleSort('STAMINA')}>체력{arrow('STAMINA')}</th>
                  {canViewPotential && <th onClick={() => toggleSort('POTENTIAL')}>잠재{arrow('POTENTIAL')}</th>}
                  <th onClick={() => toggleSort('PROPOSED_SALARY')}>영입 주급{arrow('PROPOSED_SALARY')}</th>
                  <th onClick={() => toggleSort('ASK_PRICE')}>이적료{arrow('ASK_PRICE')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedListings.map(l => {
                  const isMine       = l.SELLER_CLUB === myClubCode
                  const tooExpensive = !isMine && l.ASK_PRICE > myCash
                  return (
                    <tr
                      key={l.LISTING_CODE}
                      className={`${selected?.LISTING_CODE === l.LISTING_CODE ? 'sel' : ''}${tooExpensive ? ' too-expensive' : ''}${isMine ? ' my' : ''}`}
                      onClick={() => setSelected(l)}
                    >
                      <td>{isMine ? '⭐ 내 매물' : l.SELLER_NAME}</td>
                      <td className="pos">{l.POSITION}</td>
                      <td>{l.PLAYER_NAME}</td>
                      <td>{l.AGE}</td>
                      <td className="ovr">{l.OVERALL}</td>
                      <td className={l.STAMINA < 40 ? 'sta-cell low' : l.STAMINA < 60 ? 'sta-cell mid' : 'sta-cell'}>{l.STAMINA}</td>
                      {canViewPotential && <td>{l.POTENTIAL > 0 ? l.POTENTIAL : '-'}</td>}
                      <td className="num">₩ {l.PROPOSED_SALARY.toLocaleString()}</td>
                      <td className="num">₩ {l.ASK_PRICE.toLocaleString()}</td>
                      <td>
                        {isMine ? (
                          <button className="btn-cancel" onClick={(e) => { e.stopPropagation(); handleCancel(l) }}>취소</button>
                        ) : (
                          <>
                            <button className="btn-buy" disabled={tooExpensive} onClick={(e) => { e.stopPropagation(); handleBuy(l) }}>영입</button>
                            <button className="btn-bid" onClick={(e) => { e.stopPropagation(); handleBid(l) }}>입찰</button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {sortedListings.length === 0 && <tr><td colSpan={canViewPotential ? 10 : 9} className="muted">조건에 맞는 매물이 없습니다.</td></tr>}
              </tbody>
            </table>
          </section>

          <section className="card transfer-detail">
            <h3>매물 상세</h3>
            {selected ? (
              <>
                <div className="detail-head">
                  <span className={`detail-pos pos-${selected.POSITION}`}>{selected.POSITION}</span>
                  <span className="detail-ovr">{selected.OVERALL}</span>
                </div>
                <div className="detail-name">{selected.PLAYER_NAME}</div>
                <div className="detail-meta">
                  <span>{selected.NATIONALITY}</span> · <span>만 {selected.AGE}세</span> · <span>체력 {selected.STAMINA}</span>
                </div>
                <div className="detail-meta">
                  <span>매도: <strong>{selected.SELLER_NAME}</strong></span>
                </div>
                <div className="ask-price">
                  이적료 <strong>₩ {selected.ASK_PRICE.toLocaleString()}</strong>
                  <div className="proposed-salary">영입 후 주급 ₩ {selected.PROPOSED_SALARY.toLocaleString()} / 주 (영구 적용)</div>
                </div>
                <h4 className="detail-section">{selected.POSITION === 'GK' ? '골키퍼 능력치' : '필드 능력치'}</h4>
                <ul className="detail-stats">
                  {selected.POSITION === 'GK' ? (
                    <>
                      <li><label>반응</label><meter min={0} max={99} value={selected.REF} /><span>{selected.REF}</span></li>
                      <li><label>핸들링</label><meter min={0} max={99} value={selected.HAN} /><span>{selected.HAN}</span></li>
                      <li><label>킥</label><meter min={0} max={99} value={selected.KIK} /><span>{selected.KIK}</span></li>
                      <li><label>포지셔닝</label><meter min={0} max={99} value={selected.POS_GK} /><span>{selected.POS_GK}</span></li>
                    </>
                  ) : (
                    <>
                      <li><label>속도</label><meter min={0} max={99} value={selected.PAC} /><span>{selected.PAC}</span></li>
                      <li><label>슈팅</label><meter min={0} max={99} value={selected.SHO} /><span>{selected.SHO}</span></li>
                      <li><label>패스</label><meter min={0} max={99} value={selected.PAS} /><span>{selected.PAS}</span></li>
                      <li><label>드리블</label><meter min={0} max={99} value={selected.DRI} /><span>{selected.DRI}</span></li>
                      <li><label>수비</label><meter min={0} max={99} value={selected.DEF} /><span>{selected.DEF}</span></li>
                      <li><label>피지컬</label><meter min={0} max={99} value={selected.PHY} /><span>{selected.PHY}</span></li>
                    </>
                  )}
                </ul>
                <button className="btn-buy primary" disabled={selected.ASK_PRICE > myCash} onClick={() => handleBuy(selected)}>
                  ₩ {selected.ASK_PRICE.toLocaleString()} 에 영입
                </button>
              </>
            ) : <div className="detail-empty">매물 행을 클릭하면<br />상세 정보가 표시됩니다.</div>}
          </section>
        </div>
      )}
    </div>
  )
}
